import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  SignOut,
  ArrowLeft,
  List,
  X,
  Sun,
  Moon,
  PlusCircle,
  ArrowUpRight,
  Phone,
  CaretDown,
  BookmarkSimple,
} from "@phosphor-icons/react";
import { portalApi, PORTAL_TOKEN_KEY } from "@/lib/portalApi";
import { API } from "@/lib/api";
import { siteImg } from "@/lib/siteContent";
import { MATERIALS } from "@/lib/siteMenu";

const NAV = [
  { to: "/portal/home", label: "Dashboard", testId: "portal-nav-home", end: true },
  { to: "/portal/projects", label: "Projects", testId: "portal-nav-projects" },
  { to: "/portal/quotes", label: "Quotes", testId: "portal-nav-quotes" },
  { to: "/portal/jobs", label: "Jobs", testId: "portal-nav-jobs" },
  { to: "/portal/invoices", label: "Billing", testId: "portal-nav-invoices" },
  { to: "/portal/inventory", label: "Inventory", testId: "portal-nav-inventory", end: true, inventoryMenu: true },
  { to: "/portal/sinks", label: "Sinks", testId: "portal-nav-sinks" },
  { to: "/portal/reservations", label: "Holds", testId: "portal-nav-reservations" },
];

const initials = (name) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

export const PortalShell = ({ children, back, title, subtitle, micro, status }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [me, setMe] = useState(null);
  const [site, setSite] = useState(null);
  const [open, setOpen] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const catRef = useRef(null);
  const [catOpen, setCatOpen] = useState(false);

  useEffect(() => {
    portalApi
      .get("/me")
      .then(({ data }) => setMe(data))
      .catch(() => {
        localStorage.removeItem(PORTAL_TOKEN_KEY);
        navigate("/portal", { replace: true });
      });
  }, [navigate]);

  useEffect(() => {
    fetch(`${API}/public/site`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setSite)
      .catch(() => {});
  }, []);

  // Keep the header saved-cuts shortcut count fresh as the customer moves around.
  const refreshSavedCount = useRef(() => {});
  refreshSavedCount.current = () =>
    portalApi
      .get("/saved-cuts")
      .then(({ data }) => setSavedCount(Array.isArray(data) ? data.length : 0))
      .catch(() => {});
  useEffect(() => {
    refreshSavedCount.current();
  }, [pathname]);
  useEffect(() => {
    const onChange = () => refreshSavedCount.current();
    window.addEventListener("saved-cuts-changed", onChange);
    return () => window.removeEventListener("saved-cuts-changed", onChange);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => setOpen(false), [pathname]);
  // Close the Categories dropdown on route change or outside click.
  useEffect(() => setCatOpen(false), [pathname]);
  useEffect(() => {
    const onClick = (e) => {
      if (catRef.current && !catRef.current.contains(e.target)) setCatOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const signOut = () => {
    localStorage.removeItem(PORTAL_TOKEN_KEY);
    navigate("/portal", { replace: true });
  };

  const [userTheme, setUserTheme] = useState("");
  const [sysTheme, setSysTheme] = useState("");
  useEffect(() => {
    try {
      const s = localStorage.getItem("brioni-theme");
      if (s === "light" || s === "dark") {
        setUserTheme(s);
        return;
      }
    } catch (e) {
      /* ignore */
    }
    try {
      if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) {
        setSysTheme("dark");
      }
    } catch (e) {
      /* ignore */
    }
  }, []);
  const theme = userTheme || sysTheme || (site?.config?.appearance?.theme === "dark" ? "dark" : "light");
  const dark = theme === "dark";
  const toggleTheme = () => {
    const next = dark ? "light" : "dark";
    setUserTheme(next);
    try {
      localStorage.setItem("brioni-theme", next);
    } catch (e) {
      /* ignore */
    }
  };

  const shop = site?.shop || me?.shop || {};
  const shopName = shop.name || me?.shop?.name || "Brioni Granite";
  const logos = site?.config?.branding || {};
  const headerLogo =
    logos.logo_regular_light ||
    logos.logo_sticky_light ||
    logos.logo_sticky_dark ||
    logos.logo_regular_dark;

  const Brand = () => (
    <Link
      to="/portal/home"
      data-testid="portal-logo"
      className="flex shrink-0 items-center gap-3 text-white"
    >
      {headerLogo ? (
        <img
          src={siteImg(headerLogo)}
          alt={shopName}
          data-testid="portal-header-logo"
          className="h-9 w-auto max-w-[210px] object-contain"
        />
      ) : shop.has_logo ? (
        <img
          src={`${API}/public/logo`}
          alt={shopName}
          data-testid="portal-header-logo"
          className="h-9 w-auto max-w-[210px] object-contain brightness-0 invert"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      ) : (
        <span
          className="s-h text-base tracking-[0.2em]"
          data-testid="portal-shop-name"
        >
          {shopName}
        </span>
      )}
    </Link>
  );

  const navItem = (item) => {
    if (item.inventoryMenu) {
      return (
        <div
          key={item.label}
          className="relative"
          ref={catRef}
          onMouseEnter={() => setCatOpen(true)}
          onMouseLeave={() => setCatOpen(false)}
        >
          <NavLink
            to={item.to}
            end={item.end}
            data-testid={item.testId}
            className={({ isActive }) =>
              `s-micro relative flex items-center gap-1 whitespace-nowrap py-2 !text-[12px] transition-colors duration-300 hover:text-white ${
                isActive || catOpen ? "text-[color:var(--s-gold-bright)]" : "text-white/75"
              }`
            }
          >
            {item.label}
            <CaretDown
              size={11}
              weight="bold"
              className={`transition-transform duration-200 ${catOpen ? "rotate-180" : ""}`}
            />
          </NavLink>
          {catOpen && (
            <div className="absolute left-0 top-full z-50 w-56 pt-2" data-testid="portal-nav-inventory-menu">
              <div className="overflow-hidden rounded-xl border border-white/15 bg-[color:var(--s-dark)] py-2 shadow-[0_24px_48px_-24px_rgba(0,0,0,0.65)]">
                <Link
                  to="/portal/inventory"
                  onClick={() => setCatOpen(false)}
                  data-testid="portal-nav-cat-all"
                  className="block px-4 py-2 text-sm text-white/75 transition-colors duration-150 hover:bg-white/10 hover:text-white"
                >
                  All inventory
                </Link>
                <div className="my-1 border-t border-white/10" />
                <div className="s-micro px-4 py-1 !text-[10px] text-white/40">By material</div>
                {MATERIALS.map((m) => (
                  <Link
                    key={m.name}
                    to={`/portal/inventory?material=${encodeURIComponent(m.name)}`}
                    onClick={() => setCatOpen(false)}
                    data-testid={`portal-nav-cat-${m.name.toLowerCase()}`}
                    className="block px-4 py-2 text-sm text-white/75 transition-colors duration-150 hover:bg-white/10 hover:text-white"
                  >
                    {m.name}
                  </Link>
                ))}
                <div className="my-1 border-t border-white/10" />
                <Link
                  to="/portal/inventory?type=Remnant"
                  onClick={() => setCatOpen(false)}
                  data-testid="portal-nav-remnants"
                  className="block px-4 py-2 text-sm font-medium text-[color:var(--s-gold-bright)] transition-colors duration-150 hover:bg-white/10"
                >
                  Remnants
                </Link>
              </div>
            </div>
          )}
        </div>
      );
    }
    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.end}
        data-testid={item.testId}
        className={({ isActive }) =>
          `s-micro relative whitespace-nowrap py-2 !text-[12px] transition-colors duration-300 after:absolute after:inset-x-0 after:-bottom-0.5 after:h-px after:origin-left after:scale-x-0 after:bg-[color:var(--s-gold-bright)] after:transition-transform after:duration-300 hover:text-white hover:after:scale-x-100 ${
            isActive
              ? "text-[color:var(--s-gold-bright)] after:scale-x-100"
              : "text-white/75"
          }`
        }
      >
        {item.label}
      </NavLink>
    );
  };

  return (
    <div className={`portal-shell min-h-screen bg-slate-50 text-slate-900${dark ? " portal-dark" : ""}`} data-testid="portal-shell">
      {/* Header + mobile drawer share the public-site design tokens (.site scope). */}
      <div className={`site !bg-transparent${dark ? " s-theme-dark" : ""}`}>
        <header
          data-testid="portal-header"
          className="s-header-solid sticky top-0 z-40 text-white"
        >
          <div className="flex w-full items-center gap-6 px-6 py-4 lg:px-12">
            <Brand />

            <nav
              data-testid="portal-nav"
              className="mx-auto hidden items-center gap-5 lg:flex xl:gap-7"
            >
              {NAV.map(navItem)}
            </nav>

            <div className="hidden items-center gap-5 lg:flex">
              <button
                type="button"
                data-testid="portal-theme-toggle"
                onClick={toggleTheme}
                aria-label="Switch between light and dark"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/25 text-white transition-colors duration-200 hover:bg-white hover:text-[color:var(--s-ink)]"
              >
                {dark ? <Sun size={16} weight="bold" /> : <Moon size={16} weight="bold" />}
              </button>
              {savedCount > 0 && (
                <Link
                  to="/portal/home#saved-cuts"
                  data-testid="portal-nav-saved-cuts"
                  aria-label={`${savedCount} saved cut${savedCount === 1 ? "" : "s"}`}
                  title="Your saved cuts"
                  className="relative flex h-10 w-10 items-center justify-center rounded-full border border-white/25 text-white transition-colors duration-200 hover:bg-white hover:text-[color:var(--s-ink)]"
                >
                  <BookmarkSimple size={16} weight="bold" />
                  <span
                    data-testid="portal-saved-cuts-count"
                    className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[color:var(--s-gold-bright)] px-1 text-[10px] font-bold text-[color:var(--s-ink)]"
                  >
                    {savedCount}
                  </span>
                </Link>
              )}
              <Link
                to="/portal/request"
                data-testid="portal-nav-request"
                className="s-btn s-btn-pill whitespace-nowrap"
              >
                <PlusCircle size={15} weight="bold" /> Request quote
              </Link>

              <div className="relative" ref={menuRef}>
                <button
                  type="button"
                  data-testid="portal-user-menu"
                  onClick={() => setMenuOpen((v) => !v)}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-white/10 text-[11px] font-bold tracking-wide text-white transition-[transform,background-color] duration-150 hover:scale-105 hover:bg-white/20"
                >
                  {initials(me?.customer?.name || me?.user?.email)}
                </button>
                {menuOpen && (
                  <div
                    className="absolute right-0 mt-3 w-60 overflow-hidden rounded-2xl border border-[color:var(--s-line)] bg-white text-[color:var(--s-ink)] shadow-[0_24px_48px_-24px_rgba(0,0,0,0.5)]"
                    data-testid="portal-user-dropdown"
                  >
                    <div className="border-b border-[color:var(--s-line)] px-5 py-4">
                      <div className="truncate text-sm font-semibold text-[color:var(--s-ink)]">
                        {me?.customer?.name || "Signed in"}
                      </div>
                      <div
                        className="truncate text-xs text-[color:var(--s-mute)]"
                        data-testid="portal-user-email"
                      >
                        {me?.user?.email}
                      </div>
                    </div>
                    <Link
                      to="/portal/reservations"
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center justify-between px-5 py-3 text-sm text-[color:var(--s-mute)] transition-colors duration-150 hover:bg-[color:var(--s-bg)] hover:text-[color:var(--s-ink)]"
                    >
                      My holds
                      <ArrowUpRight size={14} weight="bold" />
                    </Link>
                    <button
                      type="button"
                      data-testid="portal-sign-out"
                      onClick={signOut}
                      className="flex w-full items-center gap-2 border-t border-[color:var(--s-line)] px-5 py-3 text-sm font-medium text-[color:var(--s-mute)] transition-colors duration-150 hover:bg-[color:var(--s-bg)] hover:text-rose-600"
                    >
                      <SignOut size={16} weight="bold" /> Sign out
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="ml-auto flex items-center gap-3 lg:hidden">
              {savedCount > 0 && (
                <Link
                  to="/portal/home#saved-cuts"
                  data-testid="portal-nav-saved-cuts-mobile"
                  aria-label={`${savedCount} saved cut${savedCount === 1 ? "" : "s"}`}
                  className="relative flex h-9 w-9 items-center justify-center rounded-full border border-white/25 text-white"
                >
                  <BookmarkSimple size={15} weight="bold" />
                  <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[color:var(--s-gold-bright)] px-1 text-[9px] font-bold text-[color:var(--s-ink)]">
                    {savedCount}
                  </span>
                </Link>
              )}
              <button
                type="button"
                data-testid="portal-theme-toggle-mobile"
                onClick={toggleTheme}
                aria-label="Switch between light and dark"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-white/25 text-white"
              >
                {dark ? <Sun size={15} weight="bold" /> : <Moon size={15} weight="bold" />}
              </button>
              <button
                type="button"
                data-testid="portal-menu-toggle"
                onClick={() => setOpen((v) => !v)}
                className="s-micro flex items-center gap-2 text-white"
              >
                {open ? (
                  <>
                    Close <X size={16} weight="bold" />
                  </>
                ) : (
                  <>
                    Menu <List size={16} weight="bold" />
                  </>
                )}
              </button>
            </div>
          </div>
        </header>

        {open && (
          <div
            className="fixed inset-0 z-[70] flex flex-col overflow-y-auto bg-[color:var(--s-dark)] px-6 pb-12 pt-5 text-white lg:hidden"
            data-testid="portal-mobile-menu"
          >
            <div className="flex items-center justify-between">
              <span className="s-h text-base tracking-[0.2em]">
                {shopName}
              </span>
              <button
                type="button"
                data-testid="portal-mobile-close"
                onClick={() => setOpen(false)}
                className="s-micro flex items-center gap-2 text-white"
              >
                Close <X size={16} weight="bold" />
              </button>
            </div>

            <div className="mt-8">
              {NAV.map((item) =>
                item.inventoryMenu ? (
                  <div
                    key={item.label}
                    data-testid={`m-${item.testId}`}
                    className="border-b border-white/10 py-5"
                  >
                    <NavLink
                      to={item.to}
                      end={item.end}
                      onClick={() => setOpen(false)}
                      className={({ isActive }) =>
                        `s-h flex items-center justify-between text-2xl ${
                          isActive ? "text-[color:var(--s-gold-bright)]" : "text-white"
                        }`
                      }
                    >
                      {item.label}
                      <ArrowUpRight size={16} weight="bold" className="opacity-40" />
                    </NavLink>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {MATERIALS.map((m) => (
                        <Link
                          key={m.name}
                          to={`/portal/inventory?material=${encodeURIComponent(m.name)}`}
                          onClick={() => setOpen(false)}
                          data-testid={`m-portal-nav-cat-${m.name.toLowerCase()}`}
                          className="rounded-full border border-white/20 px-3.5 py-1.5 text-sm text-white/80 transition-colors duration-150 hover:border-[color:var(--s-gold-bright)] hover:text-white"
                        >
                          {m.name}
                        </Link>
                      ))}
                      <Link
                        to="/portal/inventory?type=Remnant"
                        onClick={() => setOpen(false)}
                        data-testid="m-portal-nav-remnants"
                        className="rounded-full border border-[color:var(--s-gold-bright)]/50 px-3.5 py-1.5 text-sm font-medium text-[color:var(--s-gold-bright)] transition-colors duration-150 hover:bg-white/10"
                      >
                        Remnants
                      </Link>
                    </div>
                  </div>
                ) : (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    onClick={() => setOpen(false)}
                    data-testid={`m-${item.testId}`}
                    className={({ isActive }) =>
                      `s-h flex items-center justify-between border-b border-white/10 py-5 text-2xl ${
                        isActive ? "text-[color:var(--s-gold-bright)]" : "text-white"
                      }`
                    }
                  >
                    {item.label}
                    <ArrowUpRight size={16} weight="bold" className="opacity-40" />
                  </NavLink>
                ),
              )}
            </div>

            <Link
              to="/portal/request"
              onClick={() => setOpen(false)}
              data-testid="portal-nav-request-mobile"
              className="s-btn mt-10 h-12 w-full"
            >
              <PlusCircle size={16} weight="bold" /> Request a quote
            </Link>

            {shop.phone && (
              <a
                href={`tel:${shop.phone_link || shop.phone}`}
                className="s-micro mt-8 flex items-center gap-2 text-white/60"
              >
                <Phone size={13} weight="bold" /> {shop.phone}
              </a>
            )}

            <button
              type="button"
              data-testid="portal-sign-out-mobile"
              onClick={signOut}
              className="s-micro mt-6 inline-flex items-center gap-2 text-white/60 transition-colors duration-150 hover:text-rose-400"
            >
              <SignOut size={13} weight="bold" /> Sign out
            </button>
          </div>
        )}
      </div>

      {/* Full-width body */}
      <div className="w-full px-6 lg:px-12">
        <main className="portal-main min-w-0 pb-24 pt-8">
          {back && (
            <Link
              to={back}
              data-testid="portal-back"
              className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-slate-400 transition-colors duration-150 hover:text-slate-700"
            >
              <ArrowLeft size={13} weight="bold" /> Back
            </Link>
          )}
          {title && (
            <div className="mt-3 min-w-0">
              {micro && (
                <div
                  className="mb-2 text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400"
                  data-testid="portal-page-micro-label"
                >
                  {micro}
                </div>
              )}
              <div className="flex min-w-0 items-center gap-3">
                <h1
                  className="min-w-0 truncate text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl"
                  data-testid="portal-page-heading"
                >
                  {title}
                </h1>
                {status?.label && (
                  <span
                    data-testid="portal-page-status"
                    className="inline-flex shrink-0 items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-600"
                  >
                    <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${status.color || "bg-teal-500"}`} />
                    {status.label}
                  </span>
                )}
              </div>
              {subtitle && <p className="mt-2 text-sm text-slate-500">{subtitle}</p>}
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
};
